/**
 * TRANSFER OWNERSHIP (design_handoff_pro_setup README "Roles, permissions, transfer").
 *
 *   Owner    pick a current manager, type the page name, press TRANSFER TO {FIRST} NOW (red).
 *            It happens at once; the old owner stays on as an admin. An admin's open request shows
 *            at the top of the card (amber) with APPROVE · TRANSFER NOW / DECLINE.
 *   Admin    REQUEST A TRANSFER: pick the new owner (themselves or another manager), say why,
 *            type the page name, SEND REQUEST TO THE OWNER. Then WAITING FOR THE OWNER TO APPROVE
 *            with WITHDRAW REQUEST. Nothing changes until the owner approves; one open request per page.
 *
 * State comes from `GET /pro/{slug}/transfer` (role, managers, open request) and every action
 * answers with the fresh state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { proService } from '../../services';
import { INK, MUTED, INSET, CYAN, RED, PillButton, Face } from '../club/clubUi';
import { Card, CardHead, TextInput, TextArea, fieldLabel } from './setup/setupUi';

const AMBER = '#FEF9C3';
const AMBER_INK = '#713F12';

export default function TransferOwnershipPanel({ slug, pageName, currentUserId, phone = false, say, onTransferred, members: membersProp = null }) {
  const [state, setState] = useState(null); // { viewerRole, members, open }
  const [to, setTo] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // the new owner after a transfer this session

  const load = useCallback(async () => {
    if (!slug) return;
    const r = await proService.transferState(slug);
    if (r?.success) setState(r.data);
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  // ROLES & ACCESS above edits the same list — keep the candidates in step without a refetch.
  useEffect(() => { if (membersProp && state) setState((s) => ({ ...s, members: membersProp })); }, [membersProp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;
  const isOwner = state.viewerRole === 'owner';
  const isAdmin = state.viewerRole === 'admin';
  if (!isOwner && !isAdmin) return null;
  const owner = state.members.find((m) => m.isOwner);
  const ownerFirst = (owner?.displayName || 'the owner').split(' ')[0];
  const candidates = state.members.filter((m) => !m.isOwner);
  const picked = candidates.find((m) => m.userId === to) || null;
  const nameOk = confirmName.trim().toUpperCase() === (pageName || '').trim().toUpperCase();
  const ok = !!picked && nameOk && (isOwner || reason.trim().length > 0) && !busy;
  const open = state.open;
  const mine = open && open.requestedBy.userId === currentUserId;

  const act = async (fn, toast) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (!r?.success) { setError(r.error || 'That didn’t go through.'); say?.((r.error || 'THAT DIDN’T GO THROUGH').toUpperCase()); return null; }
    if (toast) say?.(toast);
    return r.data;
  };
  const send = async () => {
    if (!ok) return;
    if (isOwner) {
      const members = await act(() => proService.transferOwnership(slug, picked.userId), `${picked.displayName.toUpperCase()} IS THE OWNER`);
      if (members) { setDone(picked); onTransferred?.(picked); setState((s) => ({ ...s, viewerRole: 'admin', members })); }
    } else {
      const req = await act(() => proService.requestTransfer(slug, picked.userId, reason.trim()), `REQUEST SENT · WAITING FOR ${ownerFirst.toUpperCase()}`);
      if (req) { setState((s) => ({ ...s, open: req })); setTo(null); setConfirmName(''); setReason(''); }
    }
  };
  const decide = async (approve) => {
    const next = await act(() => (approve ? proService.approveTransfer(slug, open.id) : proService.declineTransfer(slug, open.id)), approve ? `${open.to.displayName.toUpperCase()} IS THE OWNER` : `REQUEST DECLINED · ${open.requestedBy.displayName.split(' ')[0].toUpperCase()} IS TOLD`);
    if (next) { setState(next); if (approve) { setDone(open.to); onTransferred?.(open.to); } }
  };
  const withdraw = async () => { const next = await act(() => proService.withdrawTransfer(slug, open.id), 'REQUEST WITHDRAWN'); if (next) setState(next); };

  const title = isOwner ? 'TRANSFER OWNERSHIP' : 'REQUEST A TRANSFER';
  const tag = isOwner ? 'OWNER · TAKES EFFECT AT ONCE' : 'ADMIN · THE OWNER APPROVES';

  return (
    <Card phone={phone} style={{ gap: 14 }} data-testid="transfer-card">
      <CardHead title={title} right={tag} />
      {error && <span style={{ fontSize: 11, color: RED }}>{error}</span>}

      {done && (
        <span className="flex items-center" style={{ gap: 12, padding: '14px 16px', borderRadius: 20, background: '#F0FDF4' }} data-testid="transfer-done">
          <Face src={done.avatarUrl} name={done.displayName} size={40} />
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>{done.displayName} OWNS THIS PAGE</span>
            <span style={{ fontSize: 11, lineHeight: 1.5, color: '#166534' }}>{isOwner || state.viewerRole === 'admin' ? 'You’re an admin now. You can still edit everything on the page; payouts, billing, closing and transfers are theirs.' : `${ownerFirst} approved it. Payouts, billing and closing the page are theirs now.`}</span>
          </span>
        </span>
      )}

      {!done && isOwner && open && (
        <div className="flex flex-col" style={{ gap: 12, padding: 16, borderRadius: 20, background: AMBER }} data-testid="transfer-incoming">
          <span className="flex items-center" style={{ gap: 12 }}>
            <Face src={open.requestedBy.avatarUrl} name={open.requestedBy.displayName} size={40} />
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>{open.requestedBy.displayName.split(' ')[0]} ASKED TO {open.to.userId === open.requestedBy.userId ? 'TAKE OVER THIS PAGE' : `MOVE THIS PAGE TO ${open.to.displayName}`}</span>
              <span style={{ fontSize: 11, lineHeight: 1.5, color: AMBER_INK }}>Approving makes {open.to.displayName.split(' ')[0]} the owner now; you become an admin. Declining keeps everything as it is. The request expires {new Date(open.expiresAt).toLocaleDateString()}.</span>
            </span>
          </span>
          <span style={{ padding: '10px 14px', borderRadius: 14, background: '#fff', fontSize: 12, lineHeight: 1.5 }}>“{open.reason}”</span>
          <span className="flex flex-wrap" style={{ gap: 6 }}>
            <PillButton size={44} onClick={() => decide(true)} disabled={busy} data-testid="transfer-approve">APPROVE · TRANSFER NOW</PillButton>
            <PillButton variant="dangerGhost" size={44} onClick={() => decide(false)} disabled={busy} data-testid="transfer-decline">DECLINE</PillButton>
          </span>
        </div>
      )}

      {!done && isAdmin && open && (
        <>
          <span className="flex items-center" style={{ gap: 12, padding: '14px 16px', borderRadius: 20, background: AMBER }} data-testid="transfer-pending">
            <Face src={open.to.avatarUrl} name={open.to.displayName} size={40} />
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em' }}>WAITING FOR THE OWNER TO APPROVE</span>
              <span style={{ fontSize: 11, lineHeight: 1.5, color: AMBER_INK }}>{mine ? 'You' : open.requestedBy.displayName} asked to move this page to {open.to.userId === currentUserId ? 'you' : open.to.displayName}. {ownerFirst} has to approve it — nothing changes until then. The request expires {new Date(open.expiresAt).toLocaleDateString()}.</span>
            </span>
          </span>
          {mine && <PillButton variant="danger" size={44} onClick={withdraw} disabled={busy} style={{ alignSelf: 'flex-start' }} data-testid="transfer-withdraw">WITHDRAW REQUEST</PillButton>}
        </>
      )}

      {!done && !open && (
        <>
          <span style={{ fontSize: 12, lineHeight: 1.65, color: '#374151' }}>
            {isOwner
              ? `Hand ${pageName} to someone else. It happens straight away — payouts, billing, closing the page and the next transfer move to them. You stay on as an admin unless they remove you.`
              : `Ask ${ownerFirst} to hand ${pageName} to someone — you or another manager. It only goes through once ${ownerFirst} approves it.`}
          </span>
          <span className="flex flex-col" style={{ gap: 8 }}>
            <span style={fieldLabel}>NEW OWNER</span>
            {candidates.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>Nobody else manages this page yet.</span>}
            {candidates.map((m) => {
              const on = m.userId === to;
              return (
                <button key={m.userId} type="button" aria-pressed={on} onClick={() => { setTo(on ? null : m.userId); setConfirmName(''); }} className="flex w-full items-center text-left font-mono" style={{ gap: 12, padding: '8px 14px 8px 8px', border: '2px solid transparent', borderRadius: 22, background: on ? INK : INSET, color: on ? '#fff' : INK, cursor: 'pointer' }} data-testid={`transfer-pick-${m.userId}`}>
                  <Face src={m.avatarUrl} name={m.displayName} size={40} />
                  <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{m.displayName}{m.userId === currentUserId ? ' (you)' : ''}</span><span style={{ fontSize: 10, opacity: 0.7 }}>{m.handle ? `@${m.handle} · ` : ''}{m.role.toUpperCase()}</span></span>
                  {on && <span style={{ padding: '4px 10px', borderRadius: 999, background: CYAN, color: INK, fontSize: 8, fontWeight: 800, letterSpacing: '.14em' }}>PICKED</span>}
                </button>
              );
            })}
            <span style={{ fontSize: 11, color: MUTED }}>Only people who already manage this page can take it over. Add them in ROLES &amp; ACCESS above first.</span>
          </span>
          {picked && (
            <>
              {isAdmin && (
                <label className="flex flex-col" style={{ gap: 8 }}>
                  <span style={fieldLabel}>WHY · THE OWNER SEES THIS</span>
                  <TextArea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="I run the shop day to day — I need to sort the payouts." style={{ fontSize: 13 }} data-testid="transfer-reason" />
                </label>
              )}
              <label className="flex flex-col" style={{ gap: 8 }}>
                <span style={fieldLabel}>TYPE THE PAGE NAME TO CONFIRM</span>
                <TextInput h={48} value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={pageName} style={{ letterSpacing: '.08em' }} data-testid="transfer-confirm" />
              </label>
              <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>
                {isOwner ? `${picked.displayName} becomes the owner the moment you press transfer. You can’t undo it — only they can transfer it back.` : `${ownerFirst} gets a notification and an email. Payouts and billing don’t move until the owner approves.`}
              </span>
              <span className="flex flex-wrap" style={{ gap: 6 }}>
                <PillButton size={46} onClick={send} disabled={!ok} style={{ background: isOwner ? RED : INK, opacity: ok ? 1 : 0.35 }} data-testid="transfer-send">
                  {isOwner ? `TRANSFER TO ${picked.displayName.split(' ')[0].toUpperCase()} NOW` : 'SEND REQUEST TO THE OWNER'}
                </PillButton>
                <PillButton variant="tertiary" size={46} weight={700} onClick={() => { setTo(null); setConfirmName(''); setReason(''); }}>CANCEL</PillButton>
              </span>
            </>
          )}
        </>
      )}
    </Card>
  );
}

